import { CanvasSource, ImageSource, Rectangle, Texture } from 'pixi.js';
import { requestTextureRender } from '@/utils/pixiRender';

/** Share a stable texture while image decoding completes asynchronously. */
const cache = new Map<string, Texture>();
const pending = new WeakSet<Texture>();
export const isTexturePending = (texture: Texture) => pending.has(texture);

export function getOrCreateTexture(key: string, createDataUrl: () => string): Texture {
  const existing = cache.get(key);
  if (existing) return existing;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 1;
  const placeholder = new CanvasSource({ resource: canvas });
  // Keep logical size independent of the decoded source's pixel dimensions.
  // Pixi 8 updates sprite UVs but does not reapply an explicit sprite width
  // when a dynamic texture changes size. The frame still covers the full image.
  const texture: Texture = new Texture({
    source: placeholder,
    dynamic: true,
    orig: new Rectangle(0, 0, 1, 1),
  });
  cache.set(key, texture);
  pending.add(texture);
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.onload = () => {
    pending.delete(texture);
    if (texture.destroyed) return;
    const source = new ImageSource({ resource: image });
    // Source assignment emits update synchronously (palette previews listen once).
    texture.frame.width = source.width;
    texture.frame.height = source.height;
    texture.source = source;
    texture.update();
    placeholder.destroy();
    requestTextureRender();
  };
  image.onerror = () => {
    pending.delete(texture);
  };
  image.src = createDataUrl();
  return texture;
}
export function invalidateTexture(key: string): void {
  const texture = cache.get(key);
  if (texture) {
    pending.delete(texture);
    texture.destroy(true);
    cache.delete(key);
  }
}
